import { MoreHorizontal } from "lucide-react";
import {
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
	type ReactNode,
} from "react";
import styles from "./ActionMenu.module.css";

export interface ActionMenuItem {
	/** Stable key. Also the test handle. */
	id: string;
	label: string;
	icon?: ReactNode;
	onSelect: () => void;
	/** Renders in the danger colour and is forced to the bottom of the list. */
	danger?: boolean;
	disabled?: boolean;
	/** Native tooltip — the same string the inline button used to carry. */
	title?: string;
}

export interface ActionMenuProps {
	items: ActionMenuItem[];
	/** Accessible name for the trigger. Defaults to "More actions". */
	label?: string;
	/** Extra class on the trigger so a host page can match its own toolbar. */
	triggerClassName?: string;
	/** Which edge the panel aligns to. Default "right" (toolbar at the right). */
	align?: "left" | "right";
	/**
	 * Replace the `⋯` glyph on the trigger. Use when a menu sits beside ANOTHER
	 * menu: two bare `⋯` buttons in one row are indistinguishable, and the
	 * accessible name only helps the people who are not looking at them. The
	 * wiki ribbon has exactly that — a "New" menu next to a folder-actions one.
	 */
	triggerIcon?: ReactNode;
	/** Visible text beside `triggerIcon`. Requires an icon to read as a button. */
	triggerLabel?: string;
}

/**
 * The `⋯` overflow menu — the app's ONE dropdown-of-actions primitive.
 *
 * Why it exists (2026-09-20). The wiki's article toolbar carried NINE equal-weight
 * controls in one nowrap row, so `Delete` was clipped by its container and
 * "View in graph" wrapped to three lines inside a 75px button. The sidebar's
 * action row was worse: five controls needing 465px in a 227px `overflow: hidden`
 * box, which made **Decisions and Synthesis invisible and not hit-testable** — two
 * features no user could reach at a normal desktop width. Every product this one
 * is measured against (Notion, Linear, GitHub, Obsidian) answers the same problem
 * the same way: two or three inline actions, everything else behind `⋯`. None of
 * them clips a control, because clipping is not a disclosure pattern — it is
 * silent removal.
 *
 * This is deliberately a *curated* overflow, not an automatic width-measuring one.
 * Which actions are primary is a judgement about the surface (on an article: edit
 * it, and pass a verdict on a draft), and a measuring container would reshuffle
 * that judgement every time the window moved — a toolbar whose contents depend on
 * viewport width is one you cannot build muscle memory for.
 *
 * **Never hand-roll another one.** Chat's `convMenu` in `ChatPage.tsx` is a
 * per-page copy that predates this component and should adopt it; it is the same
 * shape the repo already consolidated for Modal, Button, StatCard and
 * ImageLightbox.
 *
 * Keyboard contract: `Enter`/`Space`/`ArrowDown` on the trigger opens and focuses
 * the first item, `ArrowUp` opens at the last, arrows and `Home`/`End` move,
 * `Escape` closes, and focus always returns to the trigger so a keyboard user is
 * never dropped at the top of the document.
 */
export function ActionMenu({
	items,
	label = "More actions",
	triggerClassName,
	align = "right",
	triggerIcon,
	triggerLabel,
}: ActionMenuProps) {
	const [open, setOpen] = useState(false);
	const [activeIdx, setActiveIdx] = useState(-1);
	const rootRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const menuId = useId();

	// Danger items sink to the bottom. Destructive actions sitting between two
	// ordinary ones is how a mis-click becomes a deletion; every menu in the peer
	// set separates them, and doing it HERE means no caller can get it wrong.
	const ordered = [
		...items.filter((i) => !i.danger),
		...items.filter((i) => i.danger),
	];
	const enabledIdx = ordered
		.map((it, i) => (it.disabled ? -1 : i))
		.filter((i) => i >= 0);

	const close = useCallback(
		(restoreFocus = true) => {
			setOpen(false);
			setActiveIdx(-1);
			if (restoreFocus) triggerRef.current?.focus();
		},
		[],
	);

	// Close on outside pointer or on any Escape while open.
	useEffect(() => {
		if (!open) return;
		const onPointer = (e: PointerEvent) => {
			if (!rootRef.current?.contains(e.target as Node)) close(false);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation();
				close();
			}
		};
		document.addEventListener("pointerdown", onPointer, true);
		document.addEventListener("keydown", onKey, true);
		return () => {
			document.removeEventListener("pointerdown", onPointer, true);
			document.removeEventListener("keydown", onKey, true);
		};
	}, [open, close]);

	// Move DOM focus with the active index. Layout effect so focus lands in the
	// same frame the panel paints — otherwise a screen reader announces the
	// trigger again before the item.
	useLayoutEffect(() => {
		if (open && activeIdx >= 0) itemRefs.current[activeIdx]?.focus();
	}, [open, activeIdx]);

	const openAt = (where: "first" | "last") => {
		// Opens even when every item is disabled. Refusing to open would hide the
		// reason the user cannot act — a small copy of the clipped-toolbar bug
		// this component exists to end. Show the greyed items; focus nothing.
		setOpen(true);
		if (enabledIdx.length === 0) { setActiveIdx(-1); return; }
		setActiveIdx(where === "first" ? enabledIdx[0]! : enabledIdx[enabledIdx.length - 1]!);
	};

	const step = (delta: number) => {
		if (enabledIdx.length === 0) return;
		const pos = enabledIdx.indexOf(activeIdx);
		const next = pos < 0
			? enabledIdx[0]!
			: enabledIdx[(pos + delta + enabledIdx.length) % enabledIdx.length]!;
		setActiveIdx(next);
	};

	const run = (item: ActionMenuItem) => {
		if (item.disabled) return;
		close();
		item.onSelect();
	};

	if (ordered.length === 0) return null;

	return (
		<div className={styles.root} ref={rootRef}>
			<button
				type="button"
				ref={triggerRef}
				className={`${styles.trigger} ${triggerClassName ?? ""}`}
				aria-label={label}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={open ? menuId : undefined}
				onClick={() => (open ? close() : openAt("first"))}
				onKeyDown={(e) => {
					if (e.key === "ArrowDown") { e.preventDefault(); openAt("first"); }
					else if (e.key === "ArrowUp") { e.preventDefault(); openAt("last"); }
				}}
			>
				{triggerIcon ?? <MoreHorizontal size={15} />}
				{triggerLabel && <span className={styles.triggerLabel}>{triggerLabel}</span>}
			</button>
			{open && (
				<div
					id={menuId}
					className={`${styles.panel} ${align === "left" ? styles.alignLeft : styles.alignRight}`}
					role="menu"
					aria-label={label}
					onKeyDown={(e) => {
						if (e.key === "ArrowDown") { e.preventDefault(); step(1); }
						else if (e.key === "ArrowUp") { e.preventDefault(); step(-1); }
						else if (e.key === "Home") { e.preventDefault(); setActiveIdx(enabledIdx[0]!); }
						else if (e.key === "End") { e.preventDefault(); setActiveIdx(enabledIdx[enabledIdx.length - 1]!); }
						else if (e.key === "Tab") { close(false); }
					}}
				>
					{ordered.map((item, i) => (
						<button
							key={item.id}
							type="button"
							role="menuitem"
							data-action={item.id}
							ref={(el) => { itemRefs.current[i] = el; }}
							className={`${styles.item} ${item.danger ? styles.danger : ""}`}
							disabled={item.disabled}
							title={item.title}
							tabIndex={i === activeIdx ? 0 : -1}
							onClick={() => run(item)}
							onMouseEnter={() => !item.disabled && setActiveIdx(i)}
						>
							{item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
							<span className={styles.itemLabel}>{item.label}</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}

export default ActionMenu;
